/**
 * Effort and development-cost model behind the appliance cost calculator.
 *
 * Its own module rather than sitting in `cost.tsx`: these are the parts with
 * arithmetic worth testing, and the test harness cannot load a `.tsx`.
 */

// ---------------------------------------------------------------------------
// Human resources
// ---------------------------------------------------------------------------

/**
 * The recurring services an appliance can be sold with.
 *
 * Four services rather than one "monthly hours" figure, because they are sold
 * - and left out - separately: a customer who runs their own weekly meeting
 * still wants updates, and one who only wants updates should not be quoted a
 * support desk.
 *
 * - **updates** - rolling out new opsanio platform versions (the waves).
 * - **maintenance** (Wartung) - keeping the appliance itself healthy: patching,
 *   backups, monitoring follow-up.
 * - **support** - handling the customer's tickets and incidents.
 * - **weekly** - the standing appointment with the customer.
 */
export type ServiceId = "updates" | "maintenance" | "support" | "weekly";

/** In the order the calculator and the offer list them. */
export const SERVICE_IDS: readonly ServiceId[] = [
  "updates",
  "maintenance",
  "support",
  "weekly",
] as const;

/**
 * What a service's hours are counted per. A weekly appointment is naturally
 * stated per week, everything else per month.
 */
export type ServiceUnit = "month" | "week";

export interface ServiceLine {
  /** Whether this service is part of the deal. */
  included: boolean;
  /** Effort, per {@link per}. */
  hours: number;
  per: ServiceUnit;
}

export type ServiceLines = Record<ServiceId, ServiceLine>;

/**
 * The bundles services are sold in, each a fixed set of services.
 *
 * A bundle only decides **which** services are included; the hours stay the
 * service's own, so a heavier support allowance does not need a fifth bundle.
 * `custom` is any combination no bundle names.
 */
export type ServiceBundleId = "basic" | "standard" | "premium" | "custom";

export const SERVICE_BUNDLES: Record<
  Exclude<ServiceBundleId, "custom">,
  readonly ServiceId[]
> = {
  basic: ["updates", "maintenance"],
  standard: ["updates", "maintenance", "support"],
  premium: ["updates", "maintenance", "support", "weekly"],
};

/** Bundles in the order they are offered, cheapest first. */
export const SERVICE_BUNDLE_IDS: readonly ServiceBundleId[] = [
  "basic",
  "standard",
  "premium",
  "custom",
] as const;

/**
 * The bundle a set of services amounts to.
 *
 * Derived from what is included rather than stored next to it: a stored label
 * and the ticked services could disagree, and the offer would then name a
 * bundle the price was not built from.
 */
export const serviceBundle = (services: ServiceLines): ServiceBundleId => {
  const included = SERVICE_IDS.filter((id) => services?.[id]?.included);
  for (const [bundle, members] of Object.entries(SERVICE_BUNDLES)) {
    if (
      members.length === included.length &&
      members.every((id) => included.includes(id))
    ) {
      return bundle as ServiceBundleId;
    }
  }
  return "custom";
};

/**
 * Switch to a bundle: include exactly its services, keep every service's hours.
 * `custom` changes nothing - it is where the operator already is once they
 * tick a combination of their own.
 */
export const applyServiceBundle = (
  services: ServiceLines,
  bundle: ServiceBundleId,
): ServiceLines => {
  if (bundle === "custom") return services;
  const members = SERVICE_BUNDLES[bundle];
  return Object.fromEntries(
    SERVICE_IDS.map((id) => [
      id,
      { ...services[id], included: members.includes(id) },
    ]),
  ) as ServiceLines;
};

/**
 * The people effort one appliance costs: the one-off commissioning and the
 * recurring services it is sold with.
 */
export type NamedServiceBundle = Exclude<ServiceBundleId, "custom">;

export interface EffortConfig {
  /** One-off commissioning effort, in hours. */
  initialSetupHours: number;
  services: ServiceLines;
  /**
   * Fixed fee per month for each named bundle, in euro. It **replaces** hours x
   * rate for the bundle's services - a bundle is sold at its price, not at its
   * effort. A custom combination has no fee and is priced by effort.
   */
  bundleFees: Record<NamedServiceBundle, number>;
}

/**
 * Starting points for the quotation, not a published price list - editable
 * like every other figure here.
 */
export const DEFAULT_BUNDLE_FEES: Record<NamedServiceBundle, number> = {
  basic: 400,
  standard: 700,
  premium: 1000,
};

export const DEFAULT_SERVICES: ServiceLines = {
  updates: { included: true, hours: 1, per: "month" },
  maintenance: { included: true, hours: 2, per: "month" },
  support: { included: true, hours: 2, per: "month" },
  // Every week, half an hour.
  weekly: { included: false, hours: 0.5, per: "week" },
};

export const DEFAULT_EFFORT: EffortConfig = {
  initialSetupHours: 8,
  services: DEFAULT_SERVICES,
  bundleFees: DEFAULT_BUNDLE_FEES,
};

export interface EffortHours {
  /** Hours per month for each service; 0 for one that is not included. */
  services: Record<ServiceId, number>;
  /** What the month actually costs in hours. */
  total: number;
}

/** Round to two decimals; hours carrying float noise read as a mistake. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Hours per month one service line comes to.
 *
 * Weekly effort is converted through the **year**, not the month: "every week"
 * is 52 appointments a year, not 48. Billing it as four a month quietly loses
 * four appointments' worth of time annually.
 */
export const serviceMonthlyHours = (line?: ServiceLine): number => {
  if (!line?.included) return 0;
  const hours = Math.max(line.hours || 0, 0);
  return line.per === "week" ? (hours * 52) / 12 : hours;
};

/** Recurring hours per month, per service and in total. */
export const monthlyEffortHours = (config: EffortConfig): EffortHours => {
  const services = Object.fromEntries(
    SERVICE_IDS.map((id) => [id, serviceMonthlyHours(config?.services?.[id])]),
  ) as Record<ServiceId, number>;
  const total = SERVICE_IDS.reduce((sum, id) => sum + services[id], 0);
  return {
    services: Object.fromEntries(
      SERVICE_IDS.map((id) => [id, round2(services[id])]),
    ) as Record<ServiceId, number>,
    total: round2(total),
  };
};

// ---------------------------------------------------------------------------
// How updates reach the appliance
// ---------------------------------------------------------------------------

/**
 * The three ways an update can reach an appliance, from most to least
 * connected:
 *
 * - **online** - the appliance pulls it from the update service in our data
 *   centre. The baseline; the Update Service hours already cover it.
 * - **gateway** - it is staged on a gateway system in the customer's network
 *   and the appliance pulls it from there. One-off gateway hardware and setup,
 *   plus staging and checking every update.
 * - **airgap** - a 100 % dark site with no network path at all: every update is
 *   written to a USB drive, verified and shipped.
 */
export type UpdateMethodId = "online" | "gateway" | "airgap";

export const UPDATE_METHODS: readonly UpdateMethodId[] = [
  "online",
  "gateway",
  "airgap",
] as const;

export interface UpdateDeliveryConfig {
  method: UpdateMethodId;
  /** Updates delivered per year - releases plus emergency fixes. */
  updatesPerYear: number;
  gateway: {
    /** The gateway system itself, one-off, in euro. */
    hardwareCost: number;
    /** Setting it up, one-off, in hours. */
    setupHours: number;
    /** Staging and checking one update on it, in hours. */
    hoursPerUpdate: number;
  };
  airgap: {
    /** Downloading, verifying, writing and documenting one delivery, in hours. */
    hoursPerUpdate: number;
    /** Encrypted USB media per delivery (wear, loss, not returned), in euro. */
    mediaCostPerUpdate: number;
    /** Tracked, insured shipping per delivery, in euro. */
    shippingPerUpdate: number;
  };
}

/** Starting points for the quotation - editable like every other figure here. */
export const DEFAULT_UPDATE_DELIVERY: UpdateDeliveryConfig = {
  method: "online",
  updatesPerYear: 12,
  gateway: { hardwareCost: 1200, setupHours: 8, hoursPerUpdate: 0.5 },
  airgap: { hoursPerUpdate: 2, mediaCostPerUpdate: 25, shippingPerUpdate: 30 },
};

export interface UpdateDeliveryCost {
  method: UpdateMethodId;
  /** False when the Update Service is not part of the deal. */
  applies: boolean;
  /** Recurring, per month. */
  perMonth: number;
  /** One-off - gateway hardware and setup - recovered like the hardware. */
  oneOff: number;
  /** Extra hours a year the method costs. */
  hoursPerYear: number;
}

export const updateDeliveryCost = (
  config: UpdateDeliveryConfig | undefined,
  hourlyRate: number,
  updatesIncluded: boolean,
): UpdateDeliveryCost => {
  const method = config?.method ?? "online";
  const none = { method, applies: updatesIncluded, perMonth: 0, oneOff: 0, hoursPerYear: 0 };
  if (!config || !updatesIncluded || method === "online") return none;

  const rate = Math.max(hourlyRate, 0);
  const updates = Math.max(config.updatesPerYear || 0, 0);
  if (method === "gateway") {
    const hoursPerYear = updates * Math.max(config.gateway.hoursPerUpdate || 0, 0);
    return {
      method,
      applies: true,
      perMonth: round2((hoursPerYear * rate) / 12),
      oneOff: round2(
        Math.max(config.gateway.hardwareCost || 0, 0) +
          Math.max(config.gateway.setupHours || 0, 0) * rate,
      ),
      hoursPerYear: round2(hoursPerYear),
    };
  }
  const hoursPerYear = updates * Math.max(config.airgap.hoursPerUpdate || 0, 0);
  const materialPerYear =
    updates *
    (Math.max(config.airgap.mediaCostPerUpdate || 0, 0) +
      Math.max(config.airgap.shippingPerUpdate || 0, 0));
  return {
    method,
    applies: true,
    perMonth: round2((hoursPerYear * rate + materialPerYear) / 12),
    oneOff: 0,
    hoursPerYear: round2(hoursPerYear),
  };
};

// ---------------------------------------------------------------------------
// Service hours (coverage)
// ---------------------------------------------------------------------------

/**
 * When the customer can reach us: business days, extended hours, or 24x7.
 *
 * A **fixed fee per month** rather than more hours, because what a wider
 * window costs is the on-call standby behind it, not the work done in it - a
 * quiet 24x7 month costs the same readiness as a busy one.
 */
export type CoverageTierId = "businessDays" | "extended" | "24x7";

/** Narrowest window first. */
export const COVERAGE_TIERS: readonly CoverageTierId[] = [
  "businessDays",
  "extended",
  "24x7",
] as const;

export interface CoverageConfig {
  /** The window the deal is sold with. */
  tier: CoverageTierId;
  /**
   * Fixed fee per month for every tier, in euro. Kept per tier so switching
   * back and forth while quoting does not lose a fee somebody has adjusted.
   */
  monthlyFees: Record<CoverageTierId, number>;
  /**
   * Expected call-outs outside business hours (Mo-Fr 9-17), in hours per
   * month. Billed at the hourly rate plus the surcharges the hours fall into;
   * ignored for a window that has no hours outside business hours.
   */
  outOfHoursHours: number;
  /** Surcharges on those hours, in percent of the hourly rate. */
  surcharges: SurchargeRates;
  /**
   * Public holidays a year where the work is done. The terms count the
   * holidays at the Hamburg location (Ziffer 8.2): 9 nationwide plus
   * Reformationstag, so 10.
   */
  holidaysPerYear: number;
}

/**
 * Surcharges for work at unsocial hours, in percent of the hourly rate.
 *
 * German law sets only part of this directly. §6 Abs. 5 ArbZG requires an
 * "angemessener Zuschlag" for night work, which the BAG puts at 25 %. Sunday
 * and holiday work carry no statutory surcharge - the ArbZG asks for a
 * replacement rest day instead (§11). The rates everyone actually works with
 * are the tax-free ceilings of §3b EStG, and those are the defaults here:
 *
 * - **night** - 20-6 h, 25 % (§3b Abs. 1 Nr. 1).
 * - **deepNight** - 0-4 h, 40 %, replacing the night rate (§3b Abs. 3 Nr. 1;
 *   strictly only when the work began before midnight - assumed, because an
 *   under-priced call-out is the error nobody notices).
 * - **sunday** - 50 % (Nr. 2).
 * - **holiday** - 125 %, and on 31.12 from 14 h (Nr. 3).
 * - **special** - 150 % on 24.12 from 14 h, 25./26.12 and 1.5 (Nr. 4).
 *
 * Night and day surcharges are cumulative, as §3b allows.
 */
export interface SurchargeRates {
  night: number;
  deepNight: number;
  sunday: number;
  holiday: number;
  special: number;
}

export const DEFAULT_SURCHARGES: SurchargeRates = {
  night: 25,
  deepNight: 40,
  sunday: 50,
  holiday: 125,
  special: 150,
};

/** Which days and hours a window covers; `to` is exclusive, 0-24. */
interface CoverageWindow {
  weekdays: boolean;
  saturday: boolean;
  sunday: boolean;
  holidays: boolean;
  from: number;
  to: number;
}

/**
 * The regular service hours of the appliance terms (Ziffer 8.2): Mon-Fri
 * 09:00-17:00. Everything outside is "outside business hours" for the
 * surcharges. Must equal `SERVICE_HOURS_WINDOW` in
 * `functions/documentation/serviceLevels.ts`, which the documentation and the
 * offer quote - a test holds the two together, since the UI cannot import it.
 */
export const BUSINESS_HOURS = { from: 9, to: 17 };

/**
 * The windows, as the terms define them:
 *
 * - **businessDays** - Pflegestufe Standard (Bronze): the service hours.
 * - **extended** - Pflegestufe Enterprise (Silber): hotline Mon-Fri 08-22,
 *   outside the service hours for priority 1 only.
 * - **24x7** - beyond the terms (Ziffer 8 knows no such level); sold as a
 *   separate agreement.
 */
export const COVERAGE_WINDOWS: Record<CoverageTierId, CoverageWindow> = {
  businessDays: { weekdays: true, saturday: false, sunday: false, holidays: false, ...BUSINESS_HOURS },
  extended: { weekdays: true, saturday: false, sunday: false, holidays: false, from: 8, to: 22 },
  "24x7": { weekdays: true, saturday: true, sunday: true, holidays: true, from: 0, to: 24 },
};

type DayType =
  | "weekday"
  | "christmasEve"
  | "newYearsEve"
  | "saturday"
  | "sunday"
  | "holiday"
  | "specialHoliday";

/**
 * A model year by kind of day.
 *
 * Holidays are taken off the weekdays - most fall on one, and the error is a
 * handful of hours a year. Three of them (1.5, 25./26.12) are the 150 % days
 * and are nationwide, so they are always part of the count.
 */
const dayCounts = (holidaysPerYear: number): Record<DayType, number> => {
  const holidays = Math.min(Math.max(Math.round(holidaysPerYear || 0), 3), 20);
  return {
    weekday: 260 - holidays - 2,
    christmasEve: 1,
    newYearsEve: 1,
    saturday: 52,
    sunday: 52,
    holiday: holidays - 3,
    specialHoliday: 3,
  };
};

const isWeekdayLike = (day: DayType) =>
  day === "weekday" || day === "christmasEve" || day === "newYearsEve";

const covers = (window: CoverageWindow, day: DayType, hour: number): boolean => {
  if (hour < window.from || hour >= window.to) return false;
  if (isWeekdayLike(day)) return window.weekdays;
  if (day === "saturday") return window.saturday;
  if (day === "sunday") return window.sunday;
  return window.holidays;
};

/** Surcharge on one hour of work, in percent. */
export const hourSurchargePercent = (
  day: DayType,
  hour: number,
  rates: SurchargeRates,
): number => {
  let dayRate = 0;
  if (day === "sunday") dayRate = rates.sunday;
  else if (day === "holiday") dayRate = rates.holiday;
  else if (day === "specialHoliday") dayRate = rates.special;
  else if (day === "christmasEve" && hour >= 14) dayRate = rates.special;
  else if (day === "newYearsEve" && hour >= 14) dayRate = rates.holiday;

  const nightRate =
    hour < 4 ? rates.deepNight : hour < 6 || hour >= 20 ? rates.night : 0;

  return Math.max(dayRate, 0) + Math.max(nightRate, 0);
};

export interface OutOfHoursProfile {
  /** Hours a year the window covers outside business hours. */
  hoursPerYear: number;
  /** Average surcharge on those hours, in percent. */
  averageSurchargePercent: number;
}

/**
 * Where a window's hours outside business hours fall, and the average
 * surcharge on them.
 *
 * Call-outs are assumed to be spread evenly over those hours: nobody can say
 * when the next incident is, and the even spread is the one assumption that
 * does not flatter either side.
 */
export const outOfHoursProfile = (
  tier: CoverageTierId,
  rates: SurchargeRates,
  holidaysPerYear: number,
): OutOfHoursProfile => {
  const window = COVERAGE_WINDOWS[tier];
  const counts = dayCounts(holidaysPerYear);
  let hours = 0;
  let weighted = 0;
  for (const day of Object.keys(counts) as DayType[]) {
    for (let hour = 0; hour < 24; hour++) {
      if (!covers(window, day, hour)) continue;
      if (isWeekdayLike(day) && hour >= BUSINESS_HOURS.from && hour < BUSINESS_HOURS.to) continue;
      hours += counts[day];
      weighted += counts[day] * hourSurchargePercent(day, hour, rates);
    }
  }
  return {
    hoursPerYear: hours,
    averageSurchargePercent: hours > 0 ? round2(weighted / hours) : 0,
  };
};

export interface OutOfHoursCost {
  /** Call-out hours actually priced; 0 when the window has no such hours. */
  hours: number;
  averageSurchargePercent: number;
  /** Per month, surcharges included. */
  cost: number;
}

export const outOfHoursCost = (
  coverage: CoverageConfig | undefined,
  hourlyRate: number,
): OutOfHoursCost => {
  if (!coverage) return { hours: 0, averageSurchargePercent: 0, cost: 0 };
  const profile = outOfHoursProfile(
    coverage.tier,
    { ...DEFAULT_SURCHARGES, ...coverage.surcharges },
    coverage.holidaysPerYear ?? 10,
  );
  const hours = profile.hoursPerYear > 0 ? Math.max(coverage.outOfHoursHours || 0, 0) : 0;
  return {
    hours,
    averageSurchargePercent: profile.averageSurchargePercent,
    cost: round2(
      hours * Math.max(hourlyRate, 0) * (1 + profile.averageSurchargePercent / 100),
    ),
  };
};

/**
 * Every window carries a fee - even business days are a standby somebody
 * keeps. Starting points for the quotation, not a published price list - they
 * are editable like every other figure here.
 */
export const DEFAULT_COVERAGE: CoverageConfig = {
  tier: "businessDays",
  monthlyFees: {
    businessDays: 250,
    extended: 500,
    "24x7": 1500,
  },
  outOfHoursHours: 2,
  surcharges: DEFAULT_SURCHARGES,
  holidaysPerYear: 10,
};

/** The fixed monthly fee of the selected tier; never negative. */
export const coverageFee = (coverage?: CoverageConfig): number =>
  coverage ? Math.max(coverage.monthlyFees?.[coverage.tier] || 0, 0) : 0;

// ---------------------------------------------------------------------------
// On-site support (extension)
// ---------------------------------------------------------------------------

/**
 * One distance zone of the travel charge: every visit up to `upToKm` away
 * (one way) is charged `flat`, whatever the exact distance inside the zone.
 */
export interface TravelZone {
  upToKm: number;
  flat: number;
}

/**
 * Visits to the customer's site, on top of the remote services.
 *
 * Travel is charged by **distance zone** (Anfahrtspauschale): the one-way
 * distance picks the zone, the zone sets the flat travel charge per visit, and
 * beyond the last zone every further kilometre costs `perKmBeyond`. The travel
 * time and round-trip kilometres are derived from the distance rather than
 * typed in, because they decide the meal allowance and the §3 ArbZG check -
 * figures that must agree with the distance, not with a guess next to it.
 */
export interface OnSiteConfig {
  included: boolean;
  visitsPerYear: number;
  /** Hours on site, per visit. */
  hoursPerVisit: number;
  /** One-way distance to the site, in km. */
  distanceKm: number;
  /** Average road speed the travel time is derived with, in km/h. */
  averageSpeedKmh: number;
  /** Distance zones, nearest first. */
  travelZones: TravelZone[];
  /** Per one-way km beyond the last zone, on top of its flat charge. */
  perKmBeyond: number;
  /**
   * What the trip costs by effort, for comparison with the zone charge: travel
   * time at this share of the hourly rate, plus `costPerKm` on the round trip.
   */
  travelRatePercent: number;
  costPerKm: number;
  /** Nights away per visit; 0 is a day trip. */
  overnightStays: number;
  hotelPerNight: number;
}

/**
 * The zone charges are what the trip costs by effort at each zone's far edge -
 * 140 EUR/h, 70 km/h, 0,30 EUR/km (the tax-free business-trip rate, §9 Abs. 1
 * S. 3 Nr. 4a EStG) there and back, i.e. 4,60 EUR per one-way km - so a zone
 * never charges less than its longest trip costs. Editable like every other
 * figure here.
 */
export const DEFAULT_TRAVEL_ZONES: TravelZone[] = [
  { upToKm: 25, flat: 115 },
  { upToKm: 50, flat: 230 },
  { upToKm: 100, flat: 460 },
  { upToKm: 200, flat: 920 },
];

export const DEFAULT_ON_SITE: OnSiteConfig = {
  included: false,
  visitsPerYear: 4,
  hoursPerVisit: 4,
  distanceKm: 50,
  averageSpeedKmh: 70,
  travelZones: DEFAULT_TRAVEL_ZONES,
  perKmBeyond: 4.6,
  travelRatePercent: 100,
  costPerKm: 0.3,
  overnightStays: 0,
  hotelPerNight: 120,
};

/**
 * Statutory meal allowance (Verpflegungsmehraufwand, §9 Abs. 4a EStG): 14 EUR
 * for a day away of more than 8 hours and for the arrival and departure day of
 * a trip with overnight stays, 28 EUR for every full day in between.
 */
export const MEAL_ALLOWANCE_PARTIAL_DAY = 14;
export const MEAL_ALLOWANCE_FULL_DAY = 28;

/** §3 ArbZG: a working day may be extended to 10 hours at most. */
export const MAX_DAILY_WORKING_HOURS = 10;

export interface TravelCharge {
  /** Index into the zones, or -1 beyond the last one. */
  zone: number;
  /** The zone's reach; for beyond-the-last-zone, that zone's reach. */
  upToKm: number;
  /** What the trip is charged. */
  charge: number;
}

/**
 * The travel charge for a one-way distance.
 *
 * Zones are sorted here rather than trusted in order - an edited table is
 * easily out of order, and the first matching zone must be the nearest one.
 */
export const travelCharge = (
  distanceKm: number,
  zones: TravelZone[],
  perKmBeyond: number,
): TravelCharge => {
  const distance = Math.max(distanceKm || 0, 0);
  const sorted = [...(zones ?? [])]
    .filter((z) => z && z.upToKm > 0)
    .sort((a, b) => a.upToKm - b.upToKm);
  const index = sorted.findIndex((z) => distance <= z.upToKm);
  if (index >= 0) {
    return { zone: index, upToKm: sorted[index].upToKm, charge: Math.max(sorted[index].flat || 0, 0) };
  }
  const last = sorted[sorted.length - 1];
  const reach = last?.upToKm ?? 0;
  return {
    zone: -1,
    upToKm: reach,
    charge: round2(
      Math.max(last?.flat || 0, 0) +
        (distance - reach) * Math.max(perKmBeyond || 0, 0),
    ),
  };
};

export interface OnSiteCost {
  included: boolean;
  /** Derived from the distance. */
  roundTripKm: number;
  travelHours: number;
  travel: TravelCharge;
  /** What the same trip costs by effort - time and kilometres. */
  travelEffortCost: number;
  /** Time on site, at the hourly rate. */
  labourPerVisit: number;
  /** Zone charge plus hotel nights. */
  travelPerVisit: number;
  mealAllowancePerVisit: number;
  perVisit: number;
  perMonth: number;
  /** Working hours per day of the trip - on site plus travel. */
  hoursPerDay: number;
  /** More than §3 ArbZG allows on one day: plan an overnight stay. */
  exceedsDailyLimit: boolean;
}

const NO_ON_SITE: OnSiteCost = {
  included: false,
  roundTripKm: 0,
  travelHours: 0,
  travel: { zone: 0, upToKm: 0, charge: 0 },
  travelEffortCost: 0,
  labourPerVisit: 0,
  travelPerVisit: 0,
  mealAllowancePerVisit: 0,
  perVisit: 0,
  perMonth: 0,
  hoursPerDay: 0,
  exceedsDailyLimit: false,
};

export const onSiteCost = (
  config: OnSiteConfig | undefined,
  hourlyRate: number,
): OnSiteCost => {
  if (!config?.included) return NO_ON_SITE;
  const rate = Math.max(hourlyRate, 0);
  const onSite = Math.max(config.hoursPerVisit || 0, 0);
  const distance = Math.max(config.distanceKm || 0, 0);
  const roundTripKm = distance * 2;
  const speed = Math.max(config.averageSpeedKmh || 0, 0);
  const travelHours = speed > 0 ? roundTripKm / speed : 0;
  const nights = Math.max(Math.round(config.overnightStays || 0), 0);
  const days = nights + 1;

  const travel = travelCharge(distance, config.travelZones, config.perKmBeyond);
  const travelEffortCost =
    travelHours * rate * (Math.max(config.travelRatePercent || 0, 0) / 100) +
    roundTripKm * Math.max(config.costPerKm || 0, 0);

  const labourPerVisit = onSite * rate;
  const travelPerVisit =
    travel.charge + nights * Math.max(config.hotelPerNight || 0, 0);

  // A day trip earns the allowance only past 8 hours away; a trip with nights
  // away earns it for the arrival and departure day and in full in between.
  const mealAllowancePerVisit =
    nights === 0
      ? onSite + travelHours > 8
        ? MEAL_ALLOWANCE_PARTIAL_DAY
        : 0
      : 2 * MEAL_ALLOWANCE_PARTIAL_DAY + (nights - 1) * MEAL_ALLOWANCE_FULL_DAY;

  const perVisit = labourPerVisit + travelPerVisit + mealAllowancePerVisit;
  const hoursPerDay = (onSite + travelHours) / days;
  return {
    included: true,
    roundTripKm: round2(roundTripKm),
    travelHours: round2(travelHours),
    travel,
    travelEffortCost: round2(travelEffortCost),
    labourPerVisit: round2(labourPerVisit),
    travelPerVisit: round2(travelPerVisit),
    mealAllowancePerVisit,
    perVisit: round2(perVisit),
    perMonth: round2((perVisit * Math.max(config.visitsPerYear || 0, 0)) / 12),
    hoursPerDay: round2(hoursPerDay),
    exceedsDailyLimit: hoursPerDay > MAX_DAILY_WORKING_HOURS,
  };
};

// ---------------------------------------------------------------------------
// Development on customer request (extension)
// ---------------------------------------------------------------------------

/**
 * A yearly budget of development hours for what this customer asks for -
 * integrations, reports, adaptations.
 *
 * Separate from the development share below: that recovers what the platform
 * cost to build for everyone, this pays for work only this customer gets. A
 * budget rather than open-ended effort, so the price is fixed and so is the
 * commitment behind it.
 */
export interface CustomDevelopmentConfig {
  included: boolean;
  /** Hours in the budget, per contract year. */
  hoursPerYear: number;
  /** Development is its own rate; it need not match operations. */
  hourlyRate: number;
  /**
   * Uplift for what surrounds the coding - specification, review, testing,
   * acceptance - in percent. Requested hours are the visible part of the work.
   */
  overheadPercent: number;
  /** Whether hours not called off by the end of the contract year lapse. */
  unusedHoursExpire: boolean;
}

/** Development is billed at 175 EUR/h - above the 140 EUR/h operations rate. */
export const DEFAULT_CUSTOM_DEVELOPMENT: CustomDevelopmentConfig = {
  included: false,
  hoursPerYear: 40,
  hourlyRate: 175,
  overheadPercent: 20,
  unusedHoursExpire: true,
};

export interface CustomDevelopmentCost {
  included: boolean;
  hoursPerYear: number;
  /** Hours actually worked, overhead included. */
  effortHoursPerYear: number;
  perYear: number;
  perMonth: number;
}

export const customDevelopmentCost = (
  config: CustomDevelopmentConfig | undefined,
): CustomDevelopmentCost => {
  if (!config?.included) {
    return { included: false, hoursPerYear: 0, effortHoursPerYear: 0, perYear: 0, perMonth: 0 };
  }
  const hours = Math.max(config.hoursPerYear || 0, 0);
  const effort = hours * (1 + Math.max(config.overheadPercent || 0, 0) / 100);
  const perYear = effort * Math.max(config.hourlyRate || 0, 0);
  return {
    included: true,
    hoursPerYear: hours,
    effortHoursPerYear: round2(effort),
    perYear: round2(perYear),
    perMonth: round2(perYear / 12),
  };
};

// ---------------------------------------------------------------------------
// Platform features
// ---------------------------------------------------------------------------

/**
 * The platform features the customer is sold and sees - what the appliance
 * actually does for them. Each carries its own running cost, so a deal with
 * SIEM and AI is not priced like one with flows alone.
 *
 * The same switches as the questionnaire's Features section
 * (`features.enable.*`), which prefills them. Deliberately **not** in here: the
 * deployment-only switches in that section - BGP, developer and preview
 * access, CAPI, the customer's own domain, the OpenSearch tiers. The customer
 * does not know them, they are not sold, and they are not priced.
 */
export type FeatureId =
  | "networkFlows"
  | "certificatesManagement"
  | "siem"
  | "openVas"
  | "nuclei"
  | "compliance"
  | "guacamole"
  | "opsanioAr"
  | "opsanioAgent"
  | "ai";

export const FEATURE_IDS: readonly FeatureId[] = [
  "networkFlows",
  "certificatesManagement",
  "siem",
  "openVas",
  "nuclei",
  "compliance",
  "guacamole",
  "opsanioAr",
  "opsanioAgent",
  "ai",
] as const;

export interface FeatureLine {
  enabled: boolean;
  /** Setting it up, once, in euro - recovered like the hardware. */
  oneOff: number;
  /** Running it, per month, in euro. */
  monthly: number;
}

export type Features = Record<FeatureId, FeatureLine>;

/**
 * Starting points for the quotation, not a price list - editable like every
 * other figure here. `enabled` mirrors the questionnaire's own defaults.
 *
 * AI is the exception to "starting point": it is a paid model gateway, so its
 * monthly figure is the monthly spend cap the questionnaire sets (see
 * {@link featuresFromQuestionnaire}); 400 EUR is that cap's own default.
 */
export const DEFAULT_FEATURES: Features = {
  networkFlows: { enabled: true, oneOff: 0, monthly: 100 },
  certificatesManagement: { enabled: true, oneOff: 0, monthly: 50 },
  siem: { enabled: true, oneOff: 0, monthly: 150 },
  openVas: { enabled: false, oneOff: 0, monthly: 75 },
  nuclei: { enabled: true, oneOff: 0, monthly: 50 },
  compliance: { enabled: false, oneOff: 0, monthly: 75 },
  guacamole: { enabled: false, oneOff: 0, monthly: 50 },
  opsanioAr: { enabled: false, oneOff: 0, monthly: 100 },
  // Standard delivery: on unless the questionnaire says No.
  opsanioAgent: { enabled: true, oneOff: 0, monthly: 50 },
  ai: { enabled: false, oneOff: 0, monthly: 400 },
};

/** What the questionnaire switches on, in the calculator's terms. */
export type QuestionnaireFeatureFlags = Partial<Record<FeatureId, boolean>>;

/**
 * Prefill the switches from the questionnaire, keeping the default prices -
 * except AI, whose monthly figure is the spend cap agreed for it.
 */
export const featuresFromQuestionnaire = (
  flags: QuestionnaireFeatureFlags | null | undefined,
  aiMonthlyBudget?: number | null,
  base: Features = DEFAULT_FEATURES,
): Features => {
  const features = Object.fromEntries(
    FEATURE_IDS.map((id) => [
      id,
      { ...base[id], enabled: flags?.[id] ?? base[id].enabled },
    ]),
  ) as Features;
  if (typeof aiMonthlyBudget === "number" && aiMonthlyBudget > 0) {
    features.ai = { ...features.ai, monthly: aiMonthlyBudget };
  }
  return features;
};

export interface FeaturesCost {
  enabled: FeatureId[];
  perMonth: number;
  oneOff: number;
}

export const featuresCost = (features: Features | undefined): FeaturesCost => {
  const enabled = FEATURE_IDS.filter((id) => features?.[id]?.enabled);
  const sum = (field: "oneOff" | "monthly") =>
    enabled.reduce((total, id) => total + Math.max(features![id][field] || 0, 0), 0);
  return {
    enabled,
    perMonth: round2(sum("monthly")),
    oneOff: round2(sum("oneOff")),
  };
};

// ---------------------------------------------------------------------------
// Development cost
// ---------------------------------------------------------------------------

/**
 * What building opsanio cost, and how much of it one appliance carries.
 *
 * The platform is built once and sold many times, so a quotation that counts
 * only hardware and operating hours prices the appliance below what it cost to
 * be able to sell it at all. This recovers a share of that investment per
 * appliance, per month.
 *
 * The defaults come from the internal capital and staffing plan
 * (`documentation/business/capital-staffing-plan.de.md`, §11 Methode 3), which
 * records the cumulative investment since founding as a **confirmed** figure -
 * reported spend, not an estimate - dominated by salaries. Both other inputs
 * are commercial choices and belong to whoever is quoting.
 */
export interface DevelopmentConfig {
  /** Cumulative development investment to recover, in euro. */
  totalInvestment: number;
  /** Years to recover it over. */
  amortisationYears: number;
  /** Appliances the investment is shared across. */
  applianceCount: number;
}

/**
 * €1M is the confirmed cumulative investment since founding and the figure the
 * plan says to use as the operating floor. Five years matches the usual life of
 * capitalised software and the appliance's own longest contract term. Fifteen
 * appliances is the low end of the plan's 15-20 customer target rather than
 * today's six: amortising across today's installed base loads each early
 * customer with a share the product is not meant to carry for good.
 */
export const DEFAULT_DEVELOPMENT: DevelopmentConfig = {
  totalInvestment: 1_000_000,
  amortisationYears: 5,
  applianceCount: 15,
};

export interface DevelopmentShare {
  perYear: number;
  perMonth: number;
  /** What the selected contract term recovers. */
  perTerm: number;
}

/**
 * One appliance's share of the development investment.
 *
 * Zero - not a division by zero, and not a silent NaN that renders as an empty
 * field - when either divisor is missing. A calculator showing nothing is
 * recoverable; one showing NaN through every downstream figure is not.
 */
export const developmentShare = (
  config: DevelopmentConfig,
  contractYears: number,
): DevelopmentShare => {
  const years = Math.max(config.amortisationYears, 0);
  const appliances = Math.max(config.applianceCount, 0);
  const investment = Math.max(config.totalInvestment, 0);
  if (years === 0 || appliances === 0) {
    return { perYear: 0, perMonth: 0, perTerm: 0 };
  }
  const perYear = investment / years / appliances;
  return {
    perYear: round2(perYear),
    perMonth: round2(perYear / 12),
    perTerm: round2(perYear * Math.max(contractYears, 0)),
  };
};

// ---------------------------------------------------------------------------
// The deal the customer is actually asking for
// ---------------------------------------------------------------------------

/**
 * How a customer's asking price stands against what the appliance costs.
 *
 * Ranked against the **target margin the operator has already set**, not
 * against thresholds invented here: a house that works on 20% and one that
 * works on 40% do not agree on what a good deal is, and the calculator already
 * knows which this is.
 */
export type DealRating = "good" | "medium" | "bad" | "negative";

export interface DealAssessment {
  /** Margin the asking price actually achieves over cost, in percent. */
  marginPercent: number;
  /** Euro per month above (or below) what the appliance costs to deliver. */
  contributionPerMonth: number;
  /** What the asking price comes to over the whole term. */
  termTotal: number;
  /** Difference to the calculated price; negative means a discount is needed. */
  vsQuotedPerMonth: number;
  rating: DealRating;
}

/**
 * Rank an asking price.
 *
 * - **negative** - below what the appliance costs to deliver. Every month of
 *   this contract loses money; no volume fixes it.
 * - **bad** - covers cost, but achieves under half the target margin.
 * - **medium** - between half the target and the target.
 * - **good** - at or above the target margin.
 *
 * `cost` is the monthly figure *before* profit and discounts - hardware,
 * people and development share. Measuring against the discounted quote would
 * flatter every deal the moment someone raised the discount.
 */
export const assessDeal = (
  wishedMonthlyPrice: number,
  costPerMonth: number,
  targetMarginPercent: number,
  quotedMonthlyPrice: number,
  contractYears: number,
): DealAssessment => {
  const price = Math.max(wishedMonthlyPrice, 0);
  const contribution = price - costPerMonth;
  // A zero cost cannot be divided into; treat any price as making the target,
  // and nothing as failing it, rather than returning Infinity or NaN.
  const marginPercent =
    costPerMonth > 0 ? (contribution / costPerMonth) * 100 : price > 0 ? Infinity : 0;

  const target = Math.max(targetMarginPercent, 0);
  let rating: DealRating;
  if (contribution < 0) rating = "negative";
  else if (marginPercent >= target) rating = "good";
  else if (marginPercent >= target / 2) rating = "medium";
  else rating = "bad";

  return {
    marginPercent: Number.isFinite(marginPercent) ? round2(marginPercent) : target,
    contributionPerMonth: round2(contribution),
    termTotal: round2(price * 12 * Math.max(contractYears, 0)),
    vsQuotedPerMonth: round2(price - quotedMonthlyPrice),
    rating,
  };
};

/**
 * What a customer's stated figure is measured over.
 *
 * Customers name a price in whichever unit the conversation is in - a monthly
 * fee, an annual budget line, or the whole contract value. Forcing one of them
 * into the other two in someone's head is where an order-of-magnitude slip
 * comes from, so the basis is stated instead of assumed.
 */
export type WishedPriceBasis = "monthly" | "yearly" | "total";

export const WISHED_PRICE_BASES: readonly WishedPriceBasis[] = [
  "monthly",
  "yearly",
  "total",
] as const;

/**
 * Convert a stated asking price to the monthly figure everything else is
 * reckoned in.
 *
 * A total is spread over the **selected contract term**, so changing the term
 * re-reads the same total as a different monthly price - which is exactly what
 * it means. A term of zero would divide by zero, so it yields 0 rather than
 * Infinity.
 */
export const wishedMonthlyPrice = (
  price: number,
  basis: WishedPriceBasis,
  contractYears: number,
): number => {
  const value = Math.max(price, 0);
  const years = Math.max(contractYears, 0);
  if (basis === "monthly") return round2(value);
  if (basis === "yearly") return round2(value / 12);
  return years > 0 ? round2(value / (12 * years)) : 0;
};

// ---------------------------------------------------------------------------
// Fitting the offer to the customer's asking price
// ---------------------------------------------------------------------------

/** The price lines outside the features, each weighted on its own. */
export type PriceLineId =
  | "hardware"
  | "oneOff"
  | "services"
  | "coverage"
  | "outOfHours"
  | "onSite"
  | "customDevelopment"
  | "updateDelivery"
  | "developmentShare";

export const PRICE_LINE_IDS: readonly PriceLineId[] = [
  "hardware",
  "oneOff",
  "services",
  "coverage",
  "outOfHours",
  "onSite",
  "customDevelopment",
  "updateDelivery",
  "developmentShare",
] as const;

/** The top of the weighting scale; 0 fixes a line. */
export const MAX_PRICE_WEIGHT = 10;

/**
 * How much each price line may give way when the price has to come down to
 * what the customer asks - 0 fixes it, 10 lets it carry the most.
 *
 * A weighting, not a discount per line: the operator says which lines are
 * negotiable and how much relative to each other, and the gap to the asking
 * price is spread accordingly. Features are weighted one by one, because a
 * scanner the customer barely uses gives way more readily than SIEM.
 */
export interface PriceWeights {
  lines: Record<PriceLineId, number>;
  features: Record<FeatureId, number>;
}

/**
 * Hardware is fixed: it is bought at a price nobody here sets. Software lines
 * cost little to deliver once more and give way most; people-effort lines sit
 * in between, because a discount there is unpaid work.
 */
export const DEFAULT_PRICE_WEIGHTS: PriceWeights = {
  lines: {
    hardware: 0,
    oneOff: 2,
    services: 5,
    coverage: 5,
    outOfHours: 2,
    onSite: 3,
    customDevelopment: 5,
    updateDelivery: 2,
    developmentShare: 6,
  },
  features: Object.fromEntries(FEATURE_IDS.map((id) => [id, 8])) as Record<
    FeatureId,
    number
  >,
};

export type PriceLineKey =
  | { kind: "line"; id: PriceLineId }
  | { kind: "feature"; id: FeatureId };

export interface AdjustedPriceLine {
  key: PriceLineKey;
  weight: number;
  /** What the line costs to deliver, per month. */
  cost: number;
  /** Its share of the calculated monthly rate. */
  price: number;
  /** What it gives up to come down to the asking price. */
  reduction: number;
  /** What it gains to go up to an asking price above the rate. */
  uplift: number;
  adjusted: number;
  /** Adjusted below what it costs to deliver. */
  belowCost: boolean;
}

/**
 * Which way the offer has to move to meet the asking price: down when the
 * customer wants to pay less than calculated, up when they are willing to pay
 * more, not at all when nobody named a price or it matches the rate.
 */
export type AdjustmentDirection = "reduce" | "uplift" | "none";

export interface PriceAdjustment {
  /** True whenever the asking price differs from the calculated rate. */
  applies: boolean;
  direction: AdjustmentDirection;
  quoted: number;
  target: number;
  /** The distance between the calculated rate and the asking price. */
  gap: number;
  /** What the lines actually moved by - down or up. */
  achieved: number;
  /** Left over when the fixed lines stand in the way; 0 when reached. */
  shortfall: number;
  /** What the lines add up to after the adjustment. */
  adjustedTotal: number;
  lines: AdjustedPriceLine[];
}

/**
 * Spread the difference between the calculated rate and the asking price
 * across the lines, by weight x price.
 *
 * **Down:** no line can give up more than its whole price. What a line cannot
 * carry flows on to the others in the next round, so a small heavily weighted
 * line does not leave the gap unfilled while larger lines still have room.
 * Only when every weighted line is at zero does a shortfall remain - the fixed
 * lines are then what stands between the offer and the asking price.
 *
 * **Up:** there is no ceiling, so the surplus is always placed in full, by the
 * same weights - the lines that give way most readily are also the ones worth
 * more to this customer. With every line fixed, it is spread by price alone:
 * a customer willing to pay more is never turned away by a weighting.
 */
export const adjustToAskingPrice = (
  lines: Array<{ key: PriceLineKey; cost: number; weight: number }>,
  quoted: number,
  target: number,
): PriceAdjustment => {
  const factor = (() => {
    const cost = lines.reduce((sum, l) => sum + l.cost, 0);
    return cost > 0 ? quoted / cost : 0;
  })();
  const priced = lines.map((l) => ({
    ...l,
    weight: Math.min(Math.max(l.weight || 0, 0), MAX_PRICE_WEIGHT),
    price: l.cost * factor,
    reduction: 0,
    uplift: 0,
  }));

  // Cent-level differences are rounding, not a price anybody asked for.
  const difference = target > 0 ? target - quoted : 0;
  const direction: AdjustmentDirection =
    Math.abs(difference) < 0.005 ? "none" : difference < 0 ? "reduce" : "uplift";
  const gap = direction === "none" ? 0 : Math.abs(difference);
  let remaining = direction === "reduce" ? gap : 0;

  if (direction === "reduce") {
    let active = priced.filter((l) => l.weight > 0 && l.price > 0);
    // Each round either fills the gap or saturates at least one line, so the
    // number of lines bounds the rounds.
    for (let round = 0; round <= priced.length && remaining > 1e-9 && active.length > 0; round++) {
      const denominator = active.reduce((sum, l) => sum + l.weight * l.price, 0);
      if (denominator <= 0) break;
      let overflow = 0;
      const next: typeof active = [];
      for (const line of active) {
        const share = (remaining * line.weight * line.price) / denominator;
        const room = line.price - line.reduction;
        if (share >= room) {
          line.reduction += room;
          overflow += share - room;
        } else {
          line.reduction += share;
          next.push(line);
        }
      }
      remaining = overflow;
      active = next;
    }
  } else if (direction === "uplift") {
    const weighted = priced.filter((l) => l.weight > 0 && l.price > 0);
    const receivers = weighted.length > 0 ? weighted : priced.filter((l) => l.price > 0);
    const byWeight = weighted.length > 0;
    const denominator = receivers.reduce(
      (sum, l) => sum + (byWeight ? l.weight : 1) * l.price,
      0,
    );
    if (denominator > 0) {
      for (const line of receivers) {
        line.uplift = (gap * (byWeight ? line.weight : 1) * line.price) / denominator;
      }
    } else {
      // Nothing is priced at all - there is no line to carry the surplus.
      remaining = gap;
    }
  }

  const achieved = gap - remaining;
  const adjustedLines = priced.map((l) => {
    const adjusted = l.price - l.reduction + l.uplift;
    return {
      key: l.key,
      weight: l.weight,
      cost: round2(l.cost),
      price: round2(l.price),
      reduction: round2(l.reduction),
      uplift: round2(l.uplift),
      adjusted: round2(adjusted),
      belowCost: direction === "reduce" && adjusted < l.cost - 0.005,
    };
  });
  return {
    applies: direction !== "none",
    direction,
    quoted: round2(quoted),
    target: round2(target),
    gap: round2(gap),
    achieved: round2(achieved),
    shortfall: round2(Math.max(remaining, 0)),
    adjustedTotal: round2(
      direction === "uplift" ? quoted + achieved : quoted - achieved,
    ),
    lines: adjustedLines,
  };
};

// ---------------------------------------------------------------------------
// The whole calculation, in one place
// ---------------------------------------------------------------------------

/**
 * One priced hardware line: what a unit costs and how many are in the build.
 *
 * Structural rather than tied to the calculator's own config type, so the
 * server can price an appliance without importing a React screen.
 */
export interface HardwareLine {
  cost: number;
  count: number;
}

/** The hardware an appliance is built from, as the calculator configures it. */
export interface ApplianceHardware {
  server: HardwareLine;
  switch: HardwareLine;
  probe: HardwareLine;
  cable: {
    patch: HardwareLine;
    transceiver: HardwareLine;
    dac: HardwareLine;
  };
}

export interface HardwareTotals {
  serverTotal: number;
  switchTotal: number;
  patchCableTotal: number;
  transceiverTotal: number;
  dacTotal: number;
  cableTotal: number;
  probeTotal: number;
  /** Before the proof-of-concept credit. */
  grossTotal: number;
  /** The credit actually applied, never more than the build itself costs. */
  pocOffset: number;
  /** What the deal still has to recover. */
  baseTotal: number;
}

const lineTotal = (line?: HardwareLine): number =>
  (line?.cost || 0) * (line?.count || 0);

/**
 * What the hardware comes to.
 *
 * The PoC credit comes off the hardware the deal still has to recover, so it
 * flows through the margin and the monthly fee like any other reduction in
 * hardware cost. Floored at zero and capped at the build: a credit larger than
 * the hardware is a data-entry slip, and a negative hardware cost would make
 * the rest of the model nonsense.
 */
export const hardwareTotals = (
  hardware: ApplianceHardware,
  pocOffset: number,
): HardwareTotals => {
  const serverTotal = lineTotal(hardware?.server);
  const switchTotal = lineTotal(hardware?.switch);
  const patchCableTotal = lineTotal(hardware?.cable?.patch);
  const transceiverTotal = lineTotal(hardware?.cable?.transceiver);
  const dacTotal = lineTotal(hardware?.cable?.dac);
  const cableTotal = patchCableTotal + transceiverTotal + dacTotal;
  const probeTotal = lineTotal(hardware?.probe);
  const grossTotal = serverTotal + switchTotal + cableTotal + probeTotal;
  const applied = Math.min(Math.max(pocOffset || 0, 0), grossTotal);

  return {
    serverTotal,
    switchTotal,
    patchCableTotal,
    transceiverTotal,
    dacTotal,
    cableTotal,
    probeTotal,
    grossTotal,
    pocOffset: applied,
    baseTotal: grossTotal - applied,
  };
};

/** Everything the monthly price is derived from. */
export interface PricingInputs {
  hardware: ApplianceHardware;
  pocOffset: number;
  distributorMarginPercent: number;
  hourlyRate: number;
  effort: EffortConfig;
  /** How updates reach the appliance. */
  updateDelivery: UpdateDeliveryConfig;
  /** Service hours and their fixed monthly fee. */
  coverage: CoverageConfig;
  /** Visits to the customer's site, if the deal includes them. */
  onSite: OnSiteConfig;
  /** A development budget for customer requests, if the deal includes one. */
  customDevelopment: CustomDevelopmentConfig;
  /** The platform features the deal includes. */
  features: Features;
  development: DevelopmentConfig;
  profitMargin: number;
  /** Term discount in percent, keyed by contract years. */
  termDiscounts: Record<number, number>;
  upfrontPaymentDiscount: number;
  upfrontPayment: boolean;
  contractYears: number;
  /** How much each price line may give way to reach the asking price. */
  priceWeights: PriceWeights;
  /** What the customer has asked to pay; 0 means nobody has named a figure. */
  wishedPrice: number;
  wishedPriceBasis: WishedPriceBasis;
}

export interface PricedAppliance {
  hardware: HardwareTotals;
  hardwareCostWithMargin: number;
  effortHours: EffortHours;
  /** The bundle the included services amount to. */
  serviceBundle: ServiceBundleId;
  /** The bundle's fixed fee, or `null` for a custom combination priced by effort. */
  bundleFee: number | null;
  /** Hours x rate for the included services, whatever the bundle is billed at. */
  serviceEffortCost: number;
  setupCost: number;
  development: DevelopmentShare;
  /** What the services are billed at: the bundle fee, or effort for custom. */
  monthlyOperatingCost: number;
  /** The service hours sold, and their fixed monthly fee. */
  coverageTier: CoverageTierId;
  coverageFee: number;
  /** Call-outs outside business hours, surcharges included. */
  outOfHours: OutOfHoursCost;
  /** On-site visits, spread over the year. */
  onSite: OnSiteCost;
  /** What the chosen update path costs beyond the Update Service hours. */
  updateDelivery: UpdateDeliveryCost;
  /** The platform features switched on for this deal. */
  features: FeaturesCost;
  /** The development budget for customer requests, spread over the year. */
  customDevelopment: CustomDevelopmentCost;
  hardwareCostPerMonth: number;
  /** Before profit and before any discount - what delivery actually costs. */
  baseMonthlyCost: number;
  /** After profit, before any discount. The baseline savings measure against. */
  undiscountedMonthly: number;
  finalMonthlyCost: number;
  totalYearlyCost: number;
  totalContractCost: number;
  breakEvenMonths: number;
  totalSavings: number;
  yearlyDiscount: number;
  wishedMonthly: number;
  deal: DealAssessment;
  /** The offer fitted to the asking price, line by line. */
  adjustment: PriceAdjustment;
  /**
   * What the offer quotes. The calculated rate - unless the customer is willing
   * to pay more, in which case it is their asking price: leaving money on the
   * table the customer has offered is not a discount anybody decided on.
   * A lower asking price does not change it; that is for the approver.
   */
  offeredMonthly: number;
  offeredYearly: number;
  offeredContract: number;
  /** The amount above the calculated rate the offer carries; 0 otherwise. */
  upliftPerMonth: number;
}

/**
 * Price one appliance, end to end.
 *
 * Lives here rather than in the calculator screen because the screen is no
 * longer the only caller: the offer PDF is built from the same inputs, and an
 * approval document that disagreed with the screen it was raised from would be
 * worse than no document at all.
 */
export const priceAppliance = (inputs: PricingInputs): PricedAppliance => {
  const hardware = hardwareTotals(inputs.hardware, inputs.pocOffset);
  const contractYears = Math.max(inputs.contractYears, 0);

  const hardwareCostWithMargin =
    hardware.baseTotal * (1 + inputs.distributorMarginPercent / 100);

  // People effort: every included service, all at the same hourly rate.
  const effortHours = monthlyEffortHours(inputs.effort);
  const bundle = serviceBundle(inputs.effort.services);
  // What the included services cost by effort. The price for a named bundle
  // is its fixed fee instead; the effort figure stays alongside it so a fee
  // that no longer covers the hours behind it is visible.
  const serviceEffortCost = effortHours.total * inputs.hourlyRate;
  const bundleFee =
    bundle === "custom"
      ? null
      : Math.max(inputs.effort.bundleFees?.[bundle] ?? DEFAULT_BUNDLE_FEES[bundle], 0);
  const monthlyOperatingCost = bundleFee ?? serviceEffortCost;

  // Commissioning is a one-off. It joins the hardware in the recoverable base
  // rather than the monthly figure, which would bill it twelve times a year.
  const setupCost =
    Math.max(inputs.effort.initialSetupHours, 0) * inputs.hourlyRate;

  const development = developmentShare(inputs.development, contractYears);

  const updateDelivery = updateDeliveryCost(
    inputs.updateDelivery,
    inputs.hourlyRate,
    !!inputs.effort.services?.updates?.included,
  );

  const features = featuresCost(inputs.features);

  // One-off costs are recovered over twelve months - the gateway system and
  // the features' setup with them, since they are paid for once like the rest
  // of the build.
  const hardwareCostPerMonth =
    (hardwareCostWithMargin +
      setupCost +
      updateDelivery.oneOff +
      features.oneOff) /
    12;

  const yearlyDiscount = inputs.termDiscounts?.[contractYears] ?? 0;

  // A fixed fee, part of what delivery costs - so margin and discounts treat it
  // like every other cost, and the deal rating measures against it.
  const fixedCoverageFee = coverageFee(inputs.coverage);
  // Call-outs outside business hours, with the surcharges the hours carry.
  const outOfHours = outOfHoursCost(inputs.coverage, inputs.hourlyRate);
  const onSite = onSiteCost(inputs.onSite, inputs.hourlyRate);
  const customDevelopment = customDevelopmentCost(inputs.customDevelopment);

  const baseMonthlyCost =
    hardwareCostPerMonth +
    monthlyOperatingCost +
    fixedCoverageFee +
    outOfHours.cost +
    onSite.perMonth +
    updateDelivery.perMonth +
    features.perMonth +
    customDevelopment.perMonth +
    development.perMonth;
  const undiscountedMonthly = baseMonthlyCost * (1 + inputs.profitMargin / 100);
  const monthlyWithYearDiscount = undiscountedMonthly * (1 - yearlyDiscount / 100);

  const finalMonthlyCost = inputs.upfrontPayment
    ? monthlyWithYearDiscount * (1 - inputs.upfrontPaymentDiscount / 100)
    : monthlyWithYearDiscount;

  const breakEvenMonths =
    hardwareCostWithMargin > 0 && finalMonthlyCost > 0
      ? hardwareCostWithMargin / finalMonthlyCost
      : 0;

  const totalContractCost = finalMonthlyCost * 12 * contractYears;

  // Ranked against what the appliance costs to deliver - before profit and
  // before discounts. Measuring against the discounted quote would flatter
  // every deal the moment someone raised a discount.
  const wishedMonthly = wishedMonthlyPrice(
    inputs.wishedPrice,
    inputs.wishedPriceBasis,
    contractYears,
  );

  // Every monthly cost as its own line, so the gap to the asking price can be
  // spread by weight. Together they are exactly the base monthly cost.
  const weights = inputs.priceWeights ?? DEFAULT_PRICE_WEIGHTS;
  const lineWeight = (id: PriceLineId) =>
    weights.lines?.[id] ?? DEFAULT_PRICE_WEIGHTS.lines[id];
  const costLines: Array<{ key: PriceLineKey; cost: number; weight: number }> = [
    { key: { kind: "line", id: "hardware" }, cost: hardwareCostWithMargin / 12, weight: lineWeight("hardware") },
    {
      key: { kind: "line", id: "oneOff" },
      cost: (setupCost + updateDelivery.oneOff + features.oneOff) / 12,
      weight: lineWeight("oneOff"),
    },
    { key: { kind: "line", id: "services" }, cost: monthlyOperatingCost, weight: lineWeight("services") },
    { key: { kind: "line", id: "coverage" }, cost: fixedCoverageFee, weight: lineWeight("coverage") },
    { key: { kind: "line", id: "outOfHours" }, cost: outOfHours.cost, weight: lineWeight("outOfHours") },
    { key: { kind: "line", id: "onSite" }, cost: onSite.perMonth, weight: lineWeight("onSite") },
    { key: { kind: "line", id: "customDevelopment" }, cost: customDevelopment.perMonth, weight: lineWeight("customDevelopment") },
    { key: { kind: "line", id: "updateDelivery" }, cost: updateDelivery.perMonth, weight: lineWeight("updateDelivery") },
    ...features.enabled.map((id) => ({
      key: { kind: "feature" as const, id },
      cost: Math.max(inputs.features[id].monthly || 0, 0),
      weight: weights.features?.[id] ?? DEFAULT_PRICE_WEIGHTS.features[id],
    })),
    { key: { kind: "line", id: "developmentShare" }, cost: development.perMonth, weight: lineWeight("developmentShare") },
  ];
  const adjustment = adjustToAskingPrice(
    costLines.filter((line) => line.cost > 0),
    finalMonthlyCost,
    wishedMonthly,
  );

  const offeredMonthly =
    adjustment.direction === "uplift" ? adjustment.adjustedTotal : finalMonthlyCost;

  return {
    hardware,
    hardwareCostWithMargin,
    effortHours,
    serviceBundle: bundle,
    bundleFee,
    serviceEffortCost,
    setupCost,
    development,
    monthlyOperatingCost,
    coverageTier: inputs.coverage?.tier ?? "businessDays",
    coverageFee: fixedCoverageFee,
    outOfHours,
    onSite,
    updateDelivery,
    features,
    customDevelopment,
    hardwareCostPerMonth,
    baseMonthlyCost,
    undiscountedMonthly,
    finalMonthlyCost,
    totalYearlyCost: finalMonthlyCost * 12,
    totalContractCost,
    breakEvenMonths,
    totalSavings: undiscountedMonthly * 12 * contractYears - totalContractCost,
    yearlyDiscount,
    wishedMonthly,
    deal: assessDeal(
      wishedMonthly,
      baseMonthlyCost,
      inputs.profitMargin,
      finalMonthlyCost,
      contractYears,
    ),
    adjustment,
    offeredMonthly,
    offeredYearly: offeredMonthly * 12,
    offeredContract: offeredMonthly * 12 * contractYears,
    upliftPerMonth: offeredMonthly - finalMonthlyCost,
  };
};
